import { useEscapeKey } from '@/hooks/useEscapeKey';
import { fmtNum } from '@/lib/format';
import {
  type StockCategory,
  type StockProduct,
  fmtCzk,
  pctTone,
  splitPath,
  stockPct,
} from './types';

type Props = {
  category: StockCategory;
  /** Produkty kategorie, seřazené od nejvyšší hodnoty skladu. */
  products: StockProduct[];
  onClose: () => void;
};

/** Okno se seznamem produktů kategorie podle hodnoty skladu. */
export function ProductsModal({ category, products, onClose }: Props) {
  useEscapeKey(onClose);
  const { name } = splitPath(category.path);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button
        type="button"
        aria-label="Zavřít"
        onClick={onClose}
        className="absolute inset-0 bg-black/30 backdrop-blur-sm cursor-default"
      />
      <div className="relative w-full max-w-2xl max-h-[90vh] flex flex-col bg-surface rounded-2xl shadow-modal reveal">
        <div className="flex items-start justify-between gap-4 p-6 pb-3">
          <div className="min-w-0">
            <h2 className="text-lg font-bold text-text">Produkty v kategorii: {name}</h2>
            <p className="text-sm text-text-secondary">
              {fmtCzk(category.valueNet)} bez DPH · {fmtNum(category.qty)} ks · seřazeno podle
              hodnoty skladu
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Zavřít"
            className="shrink-0 text-text-muted hover:text-text text-2xl leading-none cursor-pointer"
          >
            ×
          </button>
        </div>
        <ul className="overflow-y-auto px-6 pb-6 divide-y divide-border-light">
          {products.map((p) => {
            const pct = stockPct(p);
            const body = (
              <>
                <div className="w-12 h-12 shrink-0 rounded-lg border border-border bg-surface overflow-hidden flex items-center justify-center">
                  {p.image_url ? (
                    <img
                      src={p.image_url}
                      alt=""
                      loading="lazy"
                      className="w-full h-full object-contain"
                    />
                  ) : (
                    <span className="text-[10px] text-text-muted">bez foto</span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-text truncate">{p.name}</div>
                  <div className="text-xs font-mono text-text-muted">{p.code}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-sm font-bold text-text">{fmtCzk(p.valueNet)}</div>
                  <div className="flex items-center justify-end gap-1.5 mt-0.5">
                    <span className="text-xs text-text-secondary">{fmtNum(p.qty)} ks</span>
                    <span
                      className={`text-[11px] font-semibold rounded px-1.5 py-0.5 ${pctTone(pct).badge}`}
                      title={`Varianty skladem: ${p.variantsInStock} / ${p.variants}`}
                    >
                      {pct} %
                    </span>
                  </div>
                </div>
              </>
            );
            return (
              <li key={p.code}>
                {p.url ? (
                  <a
                    href={p.url}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-3 py-2.5 -mx-2 px-2 rounded-lg hover:bg-surface-hover"
                  >
                    {body}
                  </a>
                ) : (
                  <div className="flex items-center gap-3 py-2.5">{body}</div>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
